import { Badge, Space, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import Private, { INCOGNITO_MASK } from "../../../../Components/Private"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import {
  alertsColumnIconMap,
  dateFormat,
  statusTextMap,
  tagColorMap,
  tagMap
} from "../../../../Utils/utils"

dayjs.extend(utc)

const { Text } = Typography

// Balance activity "Payment Source" values (see the filter dropdown / CHA1-2872).
export const SOURCE_TYPE = {
  ALERT: "Alert",
  CREDIT_REQUEST: "Credit request",
  LOOKUP: "Lookup"
}

// Each entry says where a row's Source ID column should link out to.
export const SOURCE_TYPE_NAV = {
  [SOURCE_TYPE.ALERT]: (id) => `${RoutesPaths.alerts.all}?open_alert=${id}`,
  [SOURCE_TYPE.CREDIT_REQUEST]: (id) => `${RoutesPaths.credits}?open_credit_request=${id}`,
  [SOURCE_TYPE.LOOKUP]: () => RoutesPaths.receipts.all
}

export const getColumns = ({ t, theme, handleNavigate, incognito }) => ({
  status: {
    key: "status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Status"),
        icon: alertsColumnIconMap["status"],
        stateData: state,
        sorter: true,
        key: "status"
      }),
    dataIndex: "status",
    width: 120,
    render: (_) => {
      return (
        <Tag
          color={tagMap[_?.toLowerCase()] || "default"}
          key={_} //
          style={{ margin: 0 }}
          className={"simple large"}
        >
          <Space>
            <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
            <Text>{statusTextMap[_] || _}</Text>
          </Space>
        </Tag>
      )
    }
  },
  source_id: {
    key: "source_id",
    title: (state) =>
      titleSortFilters({
        title: t("Source ID"),
        icon: alertsColumnIconMap["id"],
        stateData: state,
        key: "source_id"
      }),
    dataIndex: "source_id",
    width: 217,
    render: (_, record) => {
      const isClickable =
        record?.source_type === SOURCE_TYPE.LOOKUP ||
        ([SOURCE_TYPE.CREDIT_REQUEST, SOURCE_TYPE.ALERT].includes(record?.source_type) &&
          ["Charged", "Credited"].includes(record?.subtype) &&
          (record?.is_source_id_linkable || record?.source_type !== SOURCE_TYPE.CREDIT_REQUEST)) ||
        record.payment_url
      const onClick = () => {
        if (!isClickable) return
        if (record.payment_url) {
          window.open(record.payment_url, "_blank")
          return
        }
        const buildPath = SOURCE_TYPE_NAV[record?.source_type]
        if (buildPath) handleNavigate(buildPath(_), record?.business_account_id)
      }
      return _ || record?.invoice_id ? (
        <Text
          copyable={{
            tooltips: false,
            // Masked copy text too - otherwise the real id is a clipboard-paste away while hidden.
            text: incognito ? INCOGNITO_MASK : record.payment_url || _ || record?.invoice_id,
            icon: <Icon type="copy" />
          }}
          onClick={onClick}
          style={isClickable ? { cursor: "pointer", color: "#225AEA" } : null}
        >
          <span
            title={_ || record?.invoice_id}
            style={{
              display: "inline-block",
              maxWidth: "140px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "bottom"
            }}
          >
            <Private>{_ || record?.invoice_id}</Private>
          </span>
        </Text>
      ) : (
        "—"
      )
    }
  },
  subtype: {
    key: "subtype",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Type"),
        icon: alertsColumnIconMap["subtype"],
        stateData: state,
        sorter: true,
        key: "subtype"
      }),
    dataIndex: "subtype",
    width: 140,
    render: (value, record) => {
      if (value) {
        if (value === "Gift") {
          return (
            <Tooltip title={record?.comment}>
              <Text>{value}</Text>
            </Tooltip>
          )
        } else {
          return <Text>{statusTextMap[value] || value}</Text>
        }
      } else {
        return "—"
      }
    }
  },
  amount: {
    key: "amount",
    title: (state) =>
      titleSortFilters({
        title: t("Amount"),
        stateData: state,
        icon: alertsColumnIconMap["invoice_amount"],
        sorter: true,
        key: "amount"
      }),
    dataIndex: "amount",
    sorter: true,
    width: 128,
    render: (amount, record) => {
      const currency = record?.currency || "$"
      const isDebtCreation = amount && amount < 0
      const isRepaidDebt = record?.transaction_type === "Paid Debt"

      // Function for obtaining an absolute value with retention of decimal places
      const getAbsoluteValue = (value) => {
        if (value == null || value === "") return value

        if (typeof value === "string") {
          return value.startsWith("-") ? value.substring(1) : value
        }

        if (typeof value === "number") {
          return Math.abs(value)
        }

        return value
      }

      const displayValue = isDebtCreation ? getAbsoluteValue(amount) : amount
      const formattedAmount = `${currency} ${displayValue}`

      if (isDebtCreation) {
        return (
          <Tooltip title={t("This payment was made in debt")}>
            <Text type="danger">
              <Private>{formattedAmount}</Private>
            </Text>
          </Tooltip>
        )
      }

      if (isRepaidDebt) {
        return (
          <Tooltip title={t("This payment repaid the debt")}>
            <Text type="success">
              <Private>{formattedAmount}</Private>
            </Text>
          </Tooltip>
        )
      }

      return (
        <Text>
          <Private>{formattedAmount}</Private>
        </Text>
      )
    }
  },
  balance_before: {
    key: "balance_before",
    title: (state) =>
      titleSortFilters({
        title: "Balance Before",
        stateData: state,
        icon: alertsColumnIconMap["amount"],
        sorter: true,
        key: "balance_before"
      }),
    dataIndex: "balance_before",
    sorter: true,
    width: 148,
    render: (value) =>
      value != null ? (
        <Text>
          <Private>{`$ ${value}`}</Private>
        </Text>
      ) : (
        "—"
      )
  },
  balance_after: {
    key: "balance_after",
    title: (state) =>
      titleSortFilters({
        title: "Balance After",
        stateData: state,
        icon: alertsColumnIconMap["amount"],
        sorter: true,
        key: "balance_after"
      }),
    dataIndex: "balance_after",
    sorter: true,
    width: 148,
    render: (value) =>
      value != null ? (
        <Text>
          <Private>{`$ ${value}`}</Private>
        </Text>
      ) : (
        "—"
      )
  },
  balance_transaction_timestamp: {
    key: "balance_transaction_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Date",
        sorter: true,
        icon: alertsColumnIconMap["date"],
        stateData: state,
        key: "balance_transaction_timestamp"
      }),
    dataIndex: "balance_transaction_timestamp",
    width: 156,
    render: (_) =>
      _ ? (
        <Space direction="vertical" size={4}>
          <Text>{dayjs(_).format(dateFormat)}</Text>
          <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
        </Space>
      ) : (
        "—"
      )
  },
  card_last4: {
    key: "card_last4",
    title: (state) =>
      titleSortFilters({
        title: t("Payment Method"),
        icon: alertsColumnIconMap["card_last4"],
        stateData: state,
        key: "card_last4"
      }),
    dataIndex: "card_last4",
    render: (_, record) => (
      <Space>
        {record?.brand ? (
          <>
            <img
              src={Images[`${record?.brand?.toLowerCase()}${theme === "dark" ? "_dark" : ""}`]}
              width={20}
              height={20}
              alt={record?.brand}
            />
            <Text>••••</Text>
            {/* Last 4 digits stay visible even in Incognito - that's the one piece of card data
                considered safe to show (it's already a masked representation on its own). */}
            <Text>{_}</Text>
          </>
        ) : (
          "—"
        )}
      </Space>
    ),
    width: 168
  },
  business_account: {
    key: "business_account",
    title: (state) =>
      titleSortFilters({
        title: t("Business account"),
        icon: alertsColumnIconMap["business_account"],
        stateData: state,
        key: "business_account"
      }),
    dataIndex: "business_account",
    width: 185,
    ellipsis: true,
    render: (_) => (_ ? <Private>{_}</Private> : "—")
  }
})

export const getDefaultColumn = ({ field_id, label }) => ({
  key: field_id,
  title: (state) =>
    titleSortFilters({
      title: label,
      icon: alertsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  width: 185,
  ellipsis: true,
  render: (_) => _ || "—"
})
